import { useEffect, useState, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Trash2, Calendar, ChevronDownIcon } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { DayPicker } from 'react-day-picker'
import 'react-day-picker/style.css'

import NormalTopBar from '@/components/common/bar/NormalTopBar'
import WideButton from '@/components/common/button/WideButton'
import CardOne from '@/components/common/card/CardOne'
import { useCreateMissionMutation } from '@/hooks/queries/mission/mutation'
import type { MissionCreateBody } from '@/api/model/missionType'
import ToastMessage from '@/components/common/toast/ToastMessage'
import { useErrorHandler } from '@/contexts/ErrorContext'
import { handleError } from '@/utils/axiosConfig'
import { SESSION_STORAGE_KEYS, safeSessionStorage } from '@/constants/storageKeys'
import { sendBridgeMessage } from '@/utils/webViewBridge'

const MissionCreatePage = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [toast, setToast] = useState({ isVisible: false, message: '' })
  const errorHandler = useErrorHandler()

  const { mutate: createMission } = useCreateMissionMutation({
    onSuccess: (data) => {
      // MissionAllPage 쿼리들을 리프레시
      queryClient.invalidateQueries({ queryKey: ['missionList'] })
      queryClient.invalidateQueries({ queryKey: ['dailyMission', new Date().toISOString().split('T')[0]] })
      queryClient.invalidateQueries({
        queryKey: ['dailyMissionDateRange']
      })

      // Native 채팅에서 왔는지 확인
      if (fromNativeChat && consultationId) {
        console.log('[MissionCreatePage] Native 채팅으로 미션 등록 결과 전송')

        // 먼저 Native에 메시지 전달
        sendBridgeMessage('CHAT:MISSION_RESULT', {
          action: 'register',
          consultationId: consultationId,
          missionId: data?.data?.mission_id,
          timestamp: Date.now()
        })

        // Native가 Chat 화면을 열 시간을 준 후 WebView를 /chat으로 이동
        setTimeout(() => {
          window.location.replace('/chat')
        }, 200)
      } else {
        // 일반 WebView 플로우
        navigate(-1)
      }
    },
    onError: async error => {
      await handleError(error, errorHandler)
    }
  })
  const [missionTitle, setMissionTitle] = useState('')
  const [repeatCycle, setRepeatCycle] = useState('매주')
  const [selectedDays, setSelectedDays] = useState<string[]>([])
  const [selectedMonthDays, setSelectedMonthDays] = useState<(number | string)[]>([1])
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0])
  const [endDate, setEndDate] = useState<string | null>(null)

  // 날짜 picker 관련 state들
  const [isStartDatePickerOpen, setIsStartDatePickerOpen] = useState(false)
  const [isEndDatePickerOpen, setIsEndDatePickerOpen] = useState(false)
  const [startSelectedDate, setStartSelectedDate] = useState<Date | undefined>(new Date())
  const [endSelectedDate, setEndSelectedDate] = useState<Date | undefined>(undefined)
  const [startSelectedYear, setStartSelectedYear] = useState<number | null>(new Date().getFullYear())
  const [startSelectedMonth, setStartSelectedMonth] = useState<number | null>(new Date().getMonth() + 1)
  const [endSelectedYear, setEndSelectedYear] = useState<number | null>(null)
  const [endSelectedMonth, setEndSelectedMonth] = useState<number | null>(null)
  const [isStartYearOpen, setIsStartYearOpen] = useState(false)
  const [isStartMonthOpen, setIsStartMonthOpen] = useState(false)
  const [isEndYearOpen, setIsEndYearOpen] = useState(false)
  const [isEndMonthOpen, setIsEndMonthOpen] = useState(false)

  // refs
  const startYearDropdownRef = useRef<HTMLDivElement>(null)
  const startMonthDropdownRef = useRef<HTMLDivElement>(null)
  const startYearSelectRef = useRef<HTMLDivElement>(null)
  const startMonthSelectRef = useRef<HTMLDivElement>(null)
  const endYearDropdownRef = useRef<HTMLDivElement>(null)
  const endMonthDropdownRef = useRef<HTMLDivElement>(null)
  const endYearSelectRef = useRef<HTMLDivElement>(null)
  const endMonthSelectRef = useRef<HTMLDivElement>(null)

  // state 받아오기
  const location = useLocation()

  // Native 브릿지 이벤트로 받은 데이터를 저장할 state
  const [nativeMissionData, setNativeMissionData] = useState<{
    missionJson: any
    consultationId: number
    fromNativeChat: boolean
  } | null>(null)

  const missionJson = location.state?.missionJson || nativeMissionData?.missionJson
  const consultationId = location.state?.consultationId || nativeMissionData?.consultationId
  const fromNativeChat = location.state?.fromNativeChat || nativeMissionData?.fromNativeChat

  const repeatOptions = ['매일', '매주', '매월']

  // Native 브릿지에서 전달된 미션 데이터 복원 (sessionStorage 사용)
  useEffect(() => {
    try {
      const savedData = sessionStorage.getItem('NATIVE_MISSION_DATA')
      if (savedData) {
        console.log('[MissionCreatePage] sessionStorage에서 미션 데이터 복원:', savedData)
        const parsedData = JSON.parse(savedData)
        setNativeMissionData(parsedData)

        // 복원 후 sessionStorage 삭제 (중복 방지)
        sessionStorage.removeItem('NATIVE_MISSION_DATA')
        console.log('[MissionCreatePage] ✅ 미션 데이터 복원 완료 및 sessionStorage 정리')
      }
    } catch (error) {
      console.error('[MissionCreatePage] ❌ 미션 데이터 복원 실패:', error)
    }
  }, [])

  const weekDays = useMemo(() => ['월', '화', '수', '목', '금', '토', '일'], [])
  const monthDays = [...Array.from({ length: 31 }, (_, i) => i + 1), '말일']

  // 날짜 picker 관련 유틸리티
  const today = new Date()
  const currentYear = today.getFullYear()
  const minYear = currentYear - 10
  const maxYear = currentYear + 10

  // 년도 옵션 생성
  const yearOptions = Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i)

  // 월 옵션
  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}월`
  }))

  // 날짜 포맷 함수
  const formatDisplayDate = (date: Date) => {
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  // 월 이름을 한글로 변환
  const getMonthNameKo = (month: number) => {
    const monthNames = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월']
    return monthNames[month - 1]
  }

  // 선택된 년월에 따른 달력 표시 월 계산
  const startCalendarMonth =
    startSelectedYear && startSelectedMonth ? new Date(startSelectedYear, startSelectedMonth - 1, 1) : undefined
  const endCalendarMonth =
    endSelectedYear && endSelectedMonth ? new Date(endSelectedYear, endSelectedMonth - 1, 1) : undefined
  useEffect(() => {
    if (missionJson && consultationId) {
      setMissionTitle(missionJson.name)
      setEndDate('') // 종료일 디폴트 : 공란. / 시작일 디폴트 : 오늘날짜 (state 의 디폴트값이 오늘날짜임)
      setRepeatCycle(
        missionJson.repeat_type === 'daily' ? '매일' : missionJson.repeat_type === 'weekly' ? '매주' : '매월'
      )
      setSelectedDays(missionJson.repeat_option.days_of_week.map((day: number) => weekDays[day]))
      setSelectedMonthDays(missionJson.repeat_option.days_of_month.map((day: number) => (day === -1 ? '말일' : day)))

      // 날짜 picker 초기화
      if (missionJson.start_date) {
        const startDateObj = new Date(missionJson.start_date)
        setStartSelectedDate(startDateObj)
        setStartSelectedYear(startDateObj.getFullYear())
        setStartSelectedMonth(startDateObj.getMonth() + 1)
      }
      if (missionJson.end_date) {
        const endDateObj = new Date(missionJson.end_date)
        setEndSelectedDate(endDateObj)
        setEndSelectedYear(endDateObj.getFullYear())
        setEndSelectedMonth(endDateObj.getMonth() + 1)
      }
    }
  }, [missionJson, consultationId, weekDays])

  // 날짜 초기화 useEffect
  useEffect(() => {
    if (startDate) {
      const date = new Date(startDate)
      setStartSelectedDate(date)
      setStartSelectedYear(date.getFullYear())
      setStartSelectedMonth(date.getMonth() + 1)
    }
  }, [startDate])

  useEffect(() => {
    if (endDate) {
      const date = new Date(endDate)
      setEndSelectedDate(date)
      setEndSelectedYear(date.getFullYear())
      setEndSelectedMonth(date.getMonth() + 1)
    } else {
      setEndSelectedDate(undefined)
      setEndSelectedYear(null)
      setEndSelectedMonth(null)
    }
  }, [endDate])

  // 드롭다운 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // 시작일 드롭다운들
      if (
        startYearDropdownRef.current &&
        !startYearDropdownRef.current.contains(event.target as Node) &&
        startYearSelectRef.current &&
        !startYearSelectRef.current.contains(event.target as Node)
      ) {
        setIsStartYearOpen(false)
      }

      if (
        startMonthDropdownRef.current &&
        !startMonthDropdownRef.current.contains(event.target as Node) &&
        startMonthSelectRef.current &&
        !startMonthSelectRef.current.contains(event.target as Node)
      ) {
        setIsStartMonthOpen(false)
      }

      // 종료일 드롭다운들
      if (
        endYearDropdownRef.current &&
        !endYearDropdownRef.current.contains(event.target as Node) &&
        endYearSelectRef.current &&
        !endYearSelectRef.current.contains(event.target as Node)
      ) {
        setIsEndYearOpen(false)
      }

      if (
        endMonthDropdownRef.current &&
        !endMonthDropdownRef.current.contains(event.target as Node) &&
        endMonthSelectRef.current &&
        !endMonthSelectRef.current.contains(event.target as Node)
      ) {
        setIsEndMonthOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // 날짜 핸들러들
  const handleStartDateSelect = (date: Date | undefined) => {
    setStartSelectedDate(date)
    if (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const dateString = `${year}-${month}-${day}`
      setStartDate(dateString)
    }
    setIsStartDatePickerOpen(false)
  }

  const handleEndDateSelect = (date: Date | undefined) => {
    setEndSelectedDate(date)
    if (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const dateString = `${year}-${month}-${day}`
      setEndDate(dateString)
    }
    setIsEndDatePickerOpen(false)
  }

  const handleStartYearSelect = (year: number) => {
    setStartSelectedYear(year)
    setIsStartYearOpen(false)
    if (startSelectedMonth) {
      const newDate = new Date(year, startSelectedMonth - 1, 1)
      setStartSelectedDate(newDate)
    }
  }

  const handleStartMonthSelect = (month: number) => {
    setStartSelectedMonth(month)
    setIsStartMonthOpen(false)
    if (startSelectedYear) {
      const newDate = new Date(startSelectedYear, month - 1, 1)
      setStartSelectedDate(newDate)
    }
  }

  const handleEndYearSelect = (year: number) => {
    setEndSelectedYear(year)
    setIsEndYearOpen(false)
    if (endSelectedMonth) {
      const newDate = new Date(year, endSelectedMonth - 1, 1)
      setEndSelectedDate(newDate)
    }
  }

  const handleEndMonthSelect = (month: number) => {
    setEndSelectedMonth(month)
    setIsEndMonthOpen(false)
    if (endSelectedYear) {
      const newDate = new Date(endSelectedYear, month - 1, 1)
      setEndSelectedDate(newDate)
    }
  }

  const isValidDateString = (dateString: string) => {
    if (dateString === '') {
      return true
    }

    // YYYY.MM.DD 형식 검증
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/
    if (!dateRegex.test(dateString)) {
      return false
    }

    // 날짜 유효성 검증
    const [year, month, day] = dateString.split('-').map(Number)
    const date = new Date(year, month - 1, day)

    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
  }

  const validateDate = () => {
    if (endDate === null) {
      return true
    }

    if (isValidDateString(endDate) && isValidDateString(startDate)) {
      if (new Date(endDate) < new Date(startDate)) {
        return false
      }
      return true
    } else {
      return false
    }
  }

  const handleDayToggle = (day: string) => {
    setSelectedDays(prev => (prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]))
  }

  const handleMonthDayToggle = (day: number | string) => {
    setSelectedMonthDays(prev => (prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]))
  }

  const handleSave = async () => {
    if (missionTitle === '') {
      setToast({ isVisible: true, message: '미션명을\n입력해주세요.' })
      return
    } else if (startDate === '') {
      setToast({ isVisible: true, message: '시작일을\n입력해주세요.' })
      return
    } else if (repeatCycle === '매주' && selectedDays.length === 0) {
      setToast({ isVisible: true, message: '반복 요일을\n선택해주세요.' })
      return
    } else if (repeatCycle === '매월' && selectedMonthDays.length === 0) {
      setToast({ isVisible: true, message: '반복 날짜를\n선택해주세요.' })
      return
    } else if (endDate && new Date(endDate) < new Date(startDate)) {
      setToast({ isVisible: true, message: '종료일이 시작일보다\n이전일 수 없습니다.' })
      return
    }

    const createMissionBody: Partial<MissionCreateBody> = {
      name: missionTitle,
      start_date: startDate,
      repeat_type: repeatCycle === '매일' ? 'daily' : repeatCycle === '매주' ? 'weekly' : 'monthly'
    }
    if (endDate) {
      createMissionBody.end_date = endDate
    }
    if (repeatCycle === '매주') {
      createMissionBody.repeat_option = {
        days_of_week: selectedDays.map(day => weekDays.indexOf(day)),
        days_of_month: []
      }
    } else if (repeatCycle === '매월') {
      createMissionBody.repeat_option = {
        days_of_week: [],
        days_of_month: selectedMonthDays.map(day => (day === '말일' ? -1 : Number(day)))
      }
    }
    if (consultationId && missionJson && !fromNativeChat) {
      createMission(createMissionBody as MissionCreateBody, {
        onSuccess: () => {
          const data = {
            action: 'register',
            consultationId: consultationId,
            timestamp: Date.now()
          }
          safeSessionStorage.setJSON(SESSION_STORAGE_KEYS.MISSION_ACTION, data)
          navigate(`/chat/${consultationId}`, { replace: true })
        }
      })
    } else {
      createMission(createMissionBody as MissionCreateBody)
    }
  }

  const handleCancel = () => {
    // Native 채팅에서 왔는지 확인
    if (fromNativeChat && consultationId) {
      console.log('[MissionCreatePage] Native 채팅으로 미션 취소 결과 전송')

      // 먼저 Native에 메시지 전달
      sendBridgeMessage('CHAT:MISSION_RESULT', {
        action: 'cancel',
        consultationId: consultationId,
        timestamp: Date.now()
      })

      // Native가 Chat 화면을 열 시간을 준 후 WebView를 /chat으로 이동
      setTimeout(() => {
        window.location.replace('/chat')
      }, 200)
    } else if (consultationId && missionJson) {
      // 일반 WebView 플로우 (채팅에서 온 경우)
      navigate(`/chat/${consultationId}`, { replace: true })
      safeSessionStorage.setJSON(SESSION_STORAGE_KEYS.MISSION_ACTION, {
        action: 'cancel',
        consultationId: consultationId,
        timestamp: Date.now()
      })
    } else {
      // 일반 WebView 플로우 (기타)
      navigate(-1)
    }
  }

  return (
    <div className='min-h-screen'>
      <NormalTopBar
        title='미션 등록'
        prevButton
        onPrevButtonClick={handleCancel}
        textClassName='text-xl text-text-header font-semibold'
        boxClassName='bg-gradient-to-r from-amber-400 to-yellow-300'
      />

      <div className='p-3 space-y-3'>
        {/* 미션 제목 */}
        <CardOne className='px-3 py-5'>
          <p className='text-gray-500 font-medium text-sm mb-2'>미션명</p>
          <input
            type='text'
            value={missionTitle}
            onChange={e => setMissionTitle(e.target.value)}
            className='w-full font-medium text-gray-700 bg-transparent border-none outline-none placeholder-gray-400'
            placeholder='미션 제목을 입력하세요'
          />
        </CardOne>

        {/* 반복주기 */}
        <CardOne className='px-3 py-5'>
          <p className='text-gray-500 font-medium text-sm mb-3'>반복주기</p>
          <div className='flex gap-2 mb-4'>
            {repeatOptions.map(option => (
              <button
                key={option}
                onClick={() => setRepeatCycle(option)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  repeatCycle === option ? 'bg-amber-500 text-white' : 'bg-amber-100 text-gray-700 hover:bg-amber-200'
                }`}
              >
                {option}
              </button>
            ))}
          </div>

          {/* 요일 선택 (매주일 때만) */}
          {repeatCycle === '매주' && (
            <div className='border-t border-amber-200 pt-4'>
              <p className='text-gray-500 font-medium text-sm mb-3'>반복 요일</p>
              <div className='flex gap-1 justify-center'>
                {weekDays.map(day => (
                  <button
                    key={day}
                    onClick={() => handleDayToggle(day)}
                    className={`w-9 h-9 rounded-full text-sm font-medium transition-colors ${
                      selectedDays.includes(day)
                        ? 'bg-amber-500 text-white'
                        : 'bg-amber-100 text-gray-700 hover:bg-amber-200'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 월별 날짜 선택 (매월일 때만) */}
          {repeatCycle === '매월' && (
            <div className='border-t border-amber-200 pt-4'>
              <p className='text-gray-500 font-medium text-sm mb-3'>반복 날짜</p>
              <div className='grid grid-cols-7 gap-1'>
                {monthDays.map(day => (
                  <button
                    key={day}
                    onClick={() => handleMonthDayToggle(day)}
                    className={`h-9 w-9 rounded-full text-sm font-medium transition-colors ${
                      selectedMonthDays.includes(day)
                        ? 'bg-amber-500 text-white'
                        : 'bg-amber-100 text-gray-700 hover:bg-amber-200'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardOne>

        {/* 시작일 */}
        <CardOne className='px-3 py-5'>
          <div className='flex items-center justify-between'>
            <span className='text-gray-500 font-medium text-sm'>시작일</span>
            <div className='flex items-center justify-between gap-4'>
              <button
                type='button'
                onClick={() => setIsStartDatePickerOpen(!isStartDatePickerOpen)}
                className='text-gray-700 font-medium bg-transparent border-none outline-none text-left cursor-pointer flex items-center gap-2'
              >
                <Calendar className='w-4 h-4 text-amber-500' />
                <span>{startSelectedDate ? formatDisplayDate(startSelectedDate) : '날짜를 선택하세요'}</span>
              </button>
              <div className='size-5'></div>
            </div>
          </div>

          {/* 시작일 달력 모달 */}
          {isStartDatePickerOpen && (
            <>
              {/* 커스텀 캘린더 스타일 */}
              <style>{`
                .mission-start-calendar .rdp-day_selected {
                  background-color: #f59e0b;
                  color: white;
                }
                .mission-start-calendar .rdp-day_today {
                  background-color: rgba(245, 158, 11, 0.2);
                  color: #f59e0b;
                }
                .mission-start-calendar .rdp-day:hover {
                  background-color: rgba(245, 158, 11, 0.1);
                }
                
                .mission-start-calendar .rdp-month_caption {
                  visibility: hidden;
                  pointer-events: none;
                }
                
                .mission-start-calendar-container {
                  position: relative;
                }
                .mission-start-calendar .rdp-nav {
                  display: none;
                }
                .mission-start-calendar .rdp-day.rdp-selected > .rdp-day_button {
                  border: 1px solid #f59e0b;
                  color: white;
                  background-color: #f59e0b;
                }
                .mission-start-calendar .rdp-day.rdp-today > .rdp-day_button {
                  font-weight: bold;
                }
              `}</style>

              <div className='fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4'>
                <div className='bg-white rounded-2xl shadow-xl w-full max-w-sm'>
                  <div className='bg-amber-500 text-white text-center py-4 rounded-t-2xl'>
                    <h3 className='text-lg font-bold'>시작일 선택</h3>
                  </div>

                  <div className='p-4'>
                    <div className='mission-start-calendar-container mission-start-calendar'>
                      {/* 커스텀 캡션 - 년도/월 드롭다운 */}
                      <div className='absolute top-0 left-0 right-0 flex items-center justify-between h-12 z-10 px-2'>
                        <div className='flex items-center gap-2'>
                          {/* 년도 드롭다운 */}
                          <div className='relative'>
                            <div
                              ref={startYearSelectRef}
                              onClick={() => setIsStartYearOpen(!isStartYearOpen)}
                              className='px-3 py-2 bg-white/70 backdrop-blur-sm transition-all duration-300 text-center text-sm font-medium cursor-pointer flex items-center gap-1'
                            >
                              <span className={!startSelectedYear ? 'text-gray-400' : 'text-gray-800'}>
                                {startSelectedYear ? `${startSelectedYear}년` : '년'}
                              </span>
                              <ChevronDownIcon
                                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isStartYearOpen ? 'rotate-180' : ''}`}
                              />
                            </div>

                            {isStartYearOpen && (
                              <div
                                ref={startYearDropdownRef}
                                className='absolute z-20 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto min-w-[100px]'
                              >
                                {yearOptions.map(year => (
                                  <div
                                    key={year}
                                    onClick={() => handleStartYearSelect(year)}
                                    className='px-4 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 text-center text-gray-900 font-medium text-sm'
                                  >
                                    {year}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* 월 드롭다운 */}
                          <div className='relative'>
                            <div
                              ref={startMonthSelectRef}
                              onClick={() => setIsStartMonthOpen(!isStartMonthOpen)}
                              className='px-3 py-2 bg-white/70 backdrop-blur-sm transition-all duration-300 text-center text-sm font-medium cursor-pointer flex items-center gap-1'
                            >
                              <span className={!startSelectedMonth ? 'text-gray-400' : 'text-gray-800'}>
                                {startSelectedMonth ? getMonthNameKo(startSelectedMonth) : '월'}
                              </span>
                              <ChevronDownIcon
                                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isStartMonthOpen ? 'rotate-180' : ''}`}
                              />
                            </div>

                            {isStartMonthOpen && (
                              <div
                                ref={startMonthDropdownRef}
                                className='absolute z-20 left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto min-w-[140px]'
                              >
                                {monthOptions.map(month => (
                                  <div
                                    key={month.value}
                                    onClick={() => handleStartMonthSelect(month.value)}
                                    className='px-4 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 text-left text-gray-900 font-medium text-sm'
                                  >
                                    {getMonthNameKo(month.value)}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <DayPicker
                        mode='single'
                        selected={startSelectedDate}
                        onSelect={handleStartDateSelect}
                        month={startCalendarMonth}
                        disabled={[{ after: new Date(2030, 11, 31) }]}
                        required
                      />
                    </div>
                  </div>

                  <div className='p-4 border-t'>
                    <button
                      type='button'
                      onClick={() => setIsStartDatePickerOpen(false)}
                      className='w-full py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm'
                    >
                      닫기
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardOne>

        {/* 종료일 */}
        <CardOne className='px-3 py-5'>
          <div className='flex items-center justify-between mb-2'>
            <span className='text-gray-500 font-medium text-sm'>종료일</span>
            <div className='flex items-center justify-between gap-4'>
              <button
                type='button'
                onClick={() => setIsEndDatePickerOpen(!isEndDatePickerOpen)}
                className={`text-gray-700 font-medium bg-transparent border-none outline-none text-left cursor-pointer flex items-center gap-2 ${
                  validateDate() ? '' : 'text-red-500'
                }`}
              >
                <Calendar className='w-4 h-4 text-amber-500' />
                <span>{endSelectedDate ? formatDisplayDate(endSelectedDate) : '날짜를 선택하세요'}</span>
              </button>
              <button className='size-5 flex justify-end' onClick={() => setEndDate(null)}>
                <Trash2 className={`w-5 h-5 text-gray-400 ${endDate === null ? 'hidden' : ''}`} />
              </button>
            </div>
          </div>
          <hr className='border-amber-200 mb-2' />
          <p className='text-gray-400 text-sm'>종료일을 비워두면 계속 반복됩니다.</p>

          {/* 종료일 달력 모달 */}
          {isEndDatePickerOpen && (
            <>
              {/* 커스텀 캘린더 스타일 */}
              <style>{`
                .mission-end-calendar .rdp-day_selected {
                  background-color: #f59e0b;
                  color: white;
                }
                .mission-end-calendar .rdp-day_today {
                  background-color: rgba(245, 158, 11, 0.2);
                  color: #f59e0b;
                }
                .mission-end-calendar .rdp-day:hover {
                  background-color: rgba(245, 158, 11, 0.1);
                }
                
                .mission-end-calendar .rdp-month_caption {
                  visibility: hidden;
                  pointer-events: none;
                }
                
                .mission-end-calendar-container {
                  position: relative;
                }
                .mission-end-calendar .rdp-nav {
                  display: none;
                }
                .mission-end-calendar .rdp-day.rdp-selected > .rdp-day_button {
                  border: 1px solid #f59e0b;
                  color: white;
                  background-color: #f59e0b;
                }
                .mission-end-calendar .rdp-day.rdp-today > .rdp-day_button {
                  font-weight: bold;
                }
              `}</style>

              <div className='fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4'>
                <div className='bg-white rounded-2xl shadow-xl w-full max-w-sm'>
                  <div className='bg-amber-500 text-white text-center py-4 rounded-t-2xl'>
                    <h3 className='text-lg font-bold'>종료일 선택</h3>
                  </div>

                  <div className='p-4'>
                    <div className='mission-end-calendar-container mission-end-calendar'>
                      {/* 커스텀 캡션 - 년도/월 드롭다운 */}
                      <div className='absolute top-0 left-0 right-0 flex items-center justify-between h-12 z-10 px-2'>
                        <div className='flex items-center gap-2'>
                          {/* 년도 드롭다운 */}
                          <div className='relative'>
                            <div
                              ref={endYearSelectRef}
                              onClick={() => setIsEndYearOpen(!isEndYearOpen)}
                              className='px-3 py-2 bg-white/70 backdrop-blur-sm transition-all duration-300 text-center text-sm font-medium cursor-pointer flex items-center gap-1'
                            >
                              <span className={!endSelectedYear ? 'text-gray-400' : 'text-gray-800'}>
                                {endSelectedYear ? `${endSelectedYear}년` : '년'}
                              </span>
                              <ChevronDownIcon
                                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isEndYearOpen ? 'rotate-180' : ''}`}
                              />
                            </div>

                            {isEndYearOpen && (
                              <div
                                ref={endYearDropdownRef}
                                className='absolute z-20 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto min-w-[100px]'
                              >
                                {yearOptions.map(year => (
                                  <div
                                    key={year}
                                    onClick={() => handleEndYearSelect(year)}
                                    className='px-4 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 text-center text-gray-900 font-medium text-sm'
                                  >
                                    {year}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* 월 드롭다운 */}
                          <div className='relative'>
                            <div
                              ref={endMonthSelectRef}
                              onClick={() => setIsEndMonthOpen(!isEndMonthOpen)}
                              className='px-3 py-2 bg-white/70 backdrop-blur-sm transition-all duration-300 text-center text-sm font-medium cursor-pointer flex items-center gap-1'
                            >
                              <span className={!endSelectedMonth ? 'text-gray-400' : 'text-gray-800'}>
                                {endSelectedMonth ? getMonthNameKo(endSelectedMonth) : '월'}
                              </span>
                              <ChevronDownIcon
                                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isEndMonthOpen ? 'rotate-180' : ''}`}
                              />
                            </div>

                            {isEndMonthOpen && (
                              <div
                                ref={endMonthDropdownRef}
                                className='absolute z-20 left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto min-w-[140px]'
                              >
                                {monthOptions.map(month => (
                                  <div
                                    key={month.value}
                                    onClick={() => handleEndMonthSelect(month.value)}
                                    className='px-4 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 text-left text-gray-900 font-medium text-sm'
                                  >
                                    {getMonthNameKo(month.value)}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <DayPicker
                        mode='single'
                        selected={endSelectedDate}
                        onSelect={handleEndDateSelect}
                        month={endCalendarMonth}
                        disabled={[
                          ...(startSelectedDate ? [{ before: startSelectedDate }] : []),
                          { after: new Date(2030, 11, 31) }
                        ]}
                        required
                      />
                    </div>
                  </div>

                  <div className='p-4 border-t'>
                    <button
                      type='button'
                      onClick={() => setIsEndDatePickerOpen(false)}
                      className='w-full py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm'
                    >
                      닫기
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardOne>
      </div>

      {/* 하단 저장 버튼 */}
      <div className='px-4 pb-6 flex gap-2'>
        {location.state?.consultationId && location.state?.missionJson && (
          <WideButton
            className='bg-amber-100 text-gray-700 hover:bg-amber-200'
            handleClick={handleCancel}
            buttonText='취소'
          />
        )}
        <WideButton handleClick={handleSave} buttonText='저장' />
      </div>

      {/* 토스트 메시지 */}
      <ToastMessage
        message={toast.message}
        isVisible={toast.isVisible}
        onClose={() => setToast({ isVisible: false, message: '' })}
        addClassName='text-center whitespace-pre-line'
        duration={1000}
      />
    </div>
  )
}

export default MissionCreatePage
